import { ApiError } from '@smarton-pbx/shared';
import { useCallback, useEffect, useState } from 'react';
import type { z } from 'zod';
import { message, t } from '../i18n';

/** Loads data once (and again on reload()). */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let current = true;
    setError(undefined);
    load().then(
      (result) => current && setData(result),
      (err: unknown) => current && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { data, error, reload, setData };
}

export type FieldErrors = Record<string, string>;

/** Runs a form submission: checks input with the shared schema, then calls the API. */
export function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [fields, setFields] = useState<FieldErrors>({});

  async function submit<S extends z.ZodType, R>(schema: S, input: unknown, send: (value: z.infer<S>) => Promise<R>): Promise<R | undefined> {
    setError(undefined);
    setFields({});
    const parsed = schema.safeParse(input);
    if (!parsed.success) {
      setFields(Object.fromEntries(parsed.error.issues.map((issue) => [issue.path.join('.') || '_', message(issue.message)])));
      return undefined;
    }
    setBusy(true);
    try {
      return await send(parsed.data);
    } catch (err) {
      if (err instanceof ApiError) {
        setFields(err.fields);
        setError(err.message);
      } else {
        setError(t('Something went wrong. Try again.'));
      }
      return undefined;
    } finally {
      setBusy(false);
    }
  }

  return { submit, busy, error, fields, setError };
}
